import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, setDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { logAdminOperation } from '../../lib/adminActivityLogger';
import { PlusCircle, Edit, Trash2 } from 'lucide-react';

const Modal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-2xl">
                {children}
            </div>
        </div>
    );
};

const categories = {
    news: { name: 'お知らせ', color: 'bg-gray-100 text-gray-800' },
    maintenance: { name: 'メンテナンス', color: 'bg-yellow-100 text-yellow-800' },
    incident: { name: '障害情報', color: 'bg-red-100 text-red-800' },
    release: { name: '機能追加', color: 'bg-blue-100 text-blue-800' },
    important: { name: '重要', color: 'bg-red-100 text-red-800' },
};

const CategoryBadge = ({ category }) => {
    const categoryInfo = categories[category] || categories['news'];
    return (
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-md ${categoryInfo.color}`}>
            {categoryInfo.name}
        </span>
    );
};

const AnnouncementManagement = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
    const [formData, setFormData] = useState({ title: '', content: '', status: 'draft', category: 'news' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const announcementsCollectionRef = useCallback(() => collection(db, 'announcements'), []);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const querySnapshot = await getDocs(announcementsCollectionRef());
            const announcementsList = querySnapshot.docs.map(doc => {
                const data = doc.data();
                const rawPublishedAt = data.publishedAt?.toDate ? data.publishedAt.toDate() : (data.publishedAt ? new Date(data.publishedAt) : null);
                const rawCreatedAt = data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : null);
                const rawUpdatedAt = data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt ? new Date(data.updatedAt) : null);

                return {
                    id: doc.id,
                    ...data,
                    rawSortDate: rawCreatedAt || rawPublishedAt || rawUpdatedAt || new Date(0),
                    publishedAt: rawPublishedAt ? rawPublishedAt.toLocaleDateString('ja-JP') : '未公開',
                    createdAt: rawCreatedAt ? rawCreatedAt.toLocaleDateString('ja-JP') : 'N/A',
                };
            });

            // Sort newest first
            announcementsList.sort((a, b) => b.rawSortDate - a.rawSortDate);
            setAnnouncements(announcementsList);
        } catch (err) {
            console.error("Error fetching announcements:", err);
            setError('お知らせの読み込みに失敗しました。');
        } finally {
            setLoading(false);
        }
    }, [announcementsCollectionRef]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openModal = (announcement = null) => {
        setError(null);
        if (announcement) {
            setCurrentAnnouncement(announcement);
            setFormData({
                title: announcement.title || '',
                content: announcement.content || '',
                status: announcement.status || 'draft',
                category: announcement.category || 'news',
            });
        } else {
            setCurrentAnnouncement(null);
            setFormData({ title: '', content: '', status: 'draft', category: 'news' });
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentAnnouncement(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        if (!formData.title?.trim() || !formData.content?.trim()) {
            setError('タイトルと内容は必須です。');
            return;
        }

        try {
            setSaving(true);
            setError(null);

            const dataToSave = {
                title: formData.title.trim(),
                content: formData.content.trim(),
                status: formData.status,
                category: formData.category,
                updatedAt: serverTimestamp(),
            };

            if (formData.status === 'published') {
                if (!currentAnnouncement || currentAnnouncement.status !== 'published') {
                    dataToSave.publishedAt = serverTimestamp();
                }
            } else if (formData.status === 'draft') {
                if (!currentAnnouncement || !currentAnnouncement.publishedAt || currentAnnouncement.publishedAt === '未公開') {
                    dataToSave.publishedAt = null;
                }
            }

            if (currentAnnouncement) {
                const docRef = doc(db, 'announcements', currentAnnouncement.id);
                await setDoc(docRef, dataToSave, { merge: true });
                
                await logAdminOperation({
                    operation_type: 'announcement_update',
                    target_type: 'announcement',
                    target_id: currentAnnouncement.id,
                    target_name: formData.title,
                    reason: 'お知らせ更新'
                });
            } else {
                const newDoc = await addDoc(announcementsCollectionRef(), {
                    ...dataToSave,
                    createdAt: serverTimestamp(),
                });

                await logAdminOperation({
                    operation_type: 'announcement_create',
                    target_type: 'announcement',
                    target_id: newDoc.id,
                    target_name: formData.title,
                    reason: 'お知らせ新規作成'
                });
            }
            closeModal();
            await fetchData();
        } catch (err) {
            console.error("Error saving announcement:", err);
            setError(err.message || '保存に失敗しました。');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('このお知らせを本当に削除しますか？')) {
            try {
                const targetAnn = announcements.find(a => a.id === id);
                await deleteDoc(doc(db, 'announcements', id));

                await logAdminOperation({
                    operation_type: 'announcement_delete',
                    target_type: 'announcement',
                    target_id: id,
                    target_name: targetAnn ? targetAnn.title : '',
                    reason: 'お知らせ削除'
                });

                fetchData();
            } catch (err) {
                console.error("Error deleting announcement:", err);
                setError('削除に失敗しました。');
            }
        }
    };

    if (loading) return <div>読み込み中...</div>;

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">お知らせ管理</h1>
                <button onClick={() => openModal()} className="flex items-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700">
                    <PlusCircle size={18} className="mr-2" /> 新規作成
                </button>
            </div>

            {error && <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4">エラー: {error}</div>}

            <div className="bg-white shadow rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">タイトル</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">カテゴリー</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">作成日</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">公開日</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {announcements.map(ann => (
                            <tr key={ann.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{ann.title}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <CategoryBadge category={ann.category} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        ann.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {ann.status === 'published' ? '公開' : '下書き'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{ann.createdAt}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{ann.publishedAt}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button onClick={() => openModal(ann)} className="text-indigo-600 hover:text-indigo-900">
                                        <Edit size={18} />
                                    </button>
                                    <button onClick={() => handleDelete(ann.id)} className="text-red-600 hover:text-red-900 ml-4">
                                        <Trash2 size={18} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal isOpen={isModalOpen} onClose={closeModal}>
                <h2 className="text-xl font-semibold mb-4">{currentAnnouncement ? 'お知らせの編集' : '新規お知らせの作成'}</h2>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700">タイトル</label>
                        <input type="text" name="title" id="title" value={formData.title} onChange={handleFormChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"/>
                    </div>
                    <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700">カテゴリー</label>
                        <select name="category" id="category" value={formData.category} onChange={handleFormChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                            {Object.entries(categories).map(([key, { name }]) => (
                                <option key={key} value={key}>{name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="content" className="block text-sm font-medium text-gray-700">内容</label>
                        <textarea name="content" id="content" rows="6" value={formData.content} onChange={handleFormChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"></textarea>
                    </div>
                    <div>
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">ステータス</label>
                        <select name="status" id="status" value={formData.status} onChange={handleFormChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                            <option value="draft">下書き</option>
                            <option value="published">公開</option>
                        </select>
                    </div>
                </div>
                <div className="mt-6 flex justify-end space-x-4">
                    <button onClick={closeModal} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">キャンセル</button>
                    <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400">
                        {saving ? '保存中...' : '保存'}
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default AnnouncementManagement;
